// Persistent storage for the Apply-helper Questions tab.
//
// Each Generate run lands as one batch (timestamp + the questions the user
// submitted + the parsed answers). Newest first. Storage is browser-local
// only — drafts never leave the candidate's machine.
//
// Why a dedicated hook + persisted "consumed jobIds" set:
//   - The QuestionsCard listens for runner.status === 'done' and turns the
//     job's log into a batch. That effect can re-fire on remount (tab switch)
//     or after a browser refresh while the same jobId is still in
//     localStorage. Without an idempotency check, the same job's answers get
//     added every time — looks like duplicates to the user (or, with bad
//     timing, looks like history "vanishes" because state churn made re-render
//     drop the existing list).
//
//   - In-memory ref alone is not enough: it resets on remount/refresh.
//   - localStorage-backed Set survives refresh, so each jobId can only be
//     consumed exactly once across the lifetime of the application row.

import { useCallback, useEffect, useState } from 'react';

export interface ParsedAnswer {
  heading: string;
  body: string;
}

export interface QuestionBatch {
  id: string;             // unique, ts + random
  ts: number;             // when answers were generated (Date.now())
  jobId?: string;         // useJobRunner job that produced this batch
  questions: string;      // raw input the user submitted (for "Reuse input")
  answers: ParsedAnswer[];
}

interface NewBatchInput {
  jobId?: string;
  ts?: number;
  questions: string;
  answers: ParsedAnswer[];
}

const BATCHES_PREFIX = 'nice2job.questions.';
const CONSUMED_PREFIX = 'nice2job.questions.consumed.';

function batchesKey(appId: string): string  { return BATCHES_PREFIX + appId; }
function consumedKey(appId: string): string { return CONSUMED_PREFIX + appId; }

function loadBatches(appId: string): QuestionBatch[] {
  try {
    const raw = localStorage.getItem(batchesKey(appId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function saveBatches(appId: string, batches: QuestionBatch[]): void {
  try { localStorage.setItem(batchesKey(appId), JSON.stringify(batches)); } catch { /* noop */ }
}
function loadConsumed(appId: string): Set<string> {
  try {
    const raw = localStorage.getItem(consumedKey(appId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}
function saveConsumed(appId: string, set: Set<string>): void {
  try { localStorage.setItem(consumedKey(appId), JSON.stringify([...set])); } catch { /* noop */ }
}

export interface UseQuestionBatchesResult {
  batches: QuestionBatch[];
  addBatch: (input: NewBatchInput) => void;
  removeBatch: (id: string) => void;
  clearAll: () => void;
  isConsumed: (jobId: string | null | undefined) => boolean;
}

export function useQuestionBatches(appId: string): UseQuestionBatchesResult {
  const [batches, setBatches] = useState<QuestionBatch[]>(() => loadBatches(appId));
  // Switching apps mid-session: re-read from localStorage.
  useEffect(() => { setBatches(loadBatches(appId)); }, [appId]);

  // Consumed set is read on demand from localStorage. We don't keep it in
  // React state because it doesn't drive any rendering — it's purely a
  // dedup index for the QuestionsCard's done-watch effect.
  const isConsumed = useCallback((jobId: string | null | undefined): boolean => {
    if (!jobId) return false;
    return loadConsumed(appId).has(jobId);
  }, [appId]);

  const addBatch = useCallback((input: NewBatchInput) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const batch: QuestionBatch = {
      id,
      ts: input.ts ?? Date.now(),
      jobId: input.jobId,
      questions: input.questions,
      answers: input.answers,
    };
    setBatches((cur) => {
      const next = [batch, ...cur];
      saveBatches(appId, next);
      return next;
    });
    if (input.jobId) {
      const consumed = loadConsumed(appId);
      consumed.add(input.jobId);
      saveConsumed(appId, consumed);
    }
  }, [appId]);

  const removeBatch = useCallback((id: string) => {
    setBatches((cur) => {
      const next = cur.filter((b) => b.id !== id);
      saveBatches(appId, next);
      return next;
    });
    // We deliberately don't remove this batch's jobId from the consumed set:
    // even after the user manually deletes a batch, we still don't want it
    // re-added by an effect re-fire.
  }, [appId]);

  const clearAll = useCallback(() => {
    setBatches([]);
    try {
      localStorage.removeItem(batchesKey(appId));
      // Keep the consumed set intact so historic jobIds can never silently
      // re-populate the list. The user can still wipe it via DevTools if
      // they really want to start fresh.
    } catch { /* noop */ }
  }, [appId]);

  return { batches, addBatch, removeBatch, clearAll, isConsumed };
}
